import type { ZoneDTO } from "../api/client";
import type { ShippingSettingsInput } from "@/modules/shipping/domain/contracts";
import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const SettingsFormSchema = z.object({
  defaultZoneId: z.string().nullable(),
  defaultVariantWeightGrams: z.coerce.number().int().min(0).default(250),
});

export type ShippingSettingsFormValues = z.output<typeof SettingsFormSchema>;

const DEV_FILL: ShippingSettingsFormValues = {
  defaultZoneId: null,
  defaultVariantWeightGrams: 250,
};

type ShippingSettingsFormProps = {
  zones: ZoneDTO[];
  initialValues?: Partial<ShippingSettingsFormValues>;
  onSubmit: (values: ShippingSettingsInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function ShippingSettingsForm({
  zones,
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: ShippingSettingsFormProps) {
  const methods = RHF.useForm<ShippingSettingsFormValues>({
    resolver: zodResolver(
      SettingsFormSchema
    ) as Resolver<ShippingSettingsFormValues>,
    defaultValues: {
      defaultZoneId: null,
      defaultVariantWeightGrams: 250,
      ...initialValues,
    },
  });

  const NONE_VALUE = "__none__";
  const zoneOptions = [
    { label: "— ບໍ່ເລືອກ", value: NONE_VALUE },
    ...zones.map((z) => ({ label: z.code, value: z.id })),
  ];

  const fillDev = () => {
    methods.reset({
      ...DEV_FILL,
      defaultZoneId: zones[0]?.id ?? null,
    });
  };

  return (
    <FormRoot<ShippingSettingsFormValues>
      methods={methods as UseFormReturn<ShippingSettingsFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          defaultZoneId:
            vals.defaultZoneId === NONE_VALUE || vals.defaultZoneId === null
              ? null
              : vals.defaultZoneId ?? null,
          defaultVariantWeightGrams: vals.defaultVariantWeightGrams,
        })
      }
    >
      <div className="space-y-4" data-tourid="shipping-settings-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຕັ້ງຄ່າຄ່າສົ່ງ</h3>
          {onDevFill && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={fillDev}
            >
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <div data-tourid="shipping-settings-default-zone">
          <label className="mb-1 block font-medium text-sm">
            ໂຊນເລີ່ມຕົ້ນ (ເມື່ອບໍ່ມີໂຊນຈາກແຂວງ)
          </label>
          <RHF.Controller
            name="defaultZoneId"
            control={methods.control}
            render={({ field }) => (
              <SimpleSelect
                placeholder="— ບໍ່ເລືອກ"
                className="w-full max-w-xs"
                value={
                  field.value === null ? NONE_VALUE : field.value ?? NONE_VALUE
                }
                onValueChange={(v) =>
                  field.onChange(v === NONE_VALUE ? null : v)
                }
                options={zoneOptions}
              />
            )}
          />
        </div>
        <div data-tourid="shipping-settings-variant-weight">
          <FormInput
            name="defaultVariantWeightGrams"
            label="ນ້ຳໜັກ variant ເລີ່ມຕົ້ນ (ກຣາມ) — ໃຊ້ເມື່ອ variant ບໍ່ມີນ້ຳໜັກ"
            type="number"
            min={0}
          />
        </div>
        <div className="flex gap-2 pt-2" data-tourid="shipping-settings-submit">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
